import { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Textarea } from 'components/ui/textarea';
import { Badge } from 'components/ui/badge';
import { Avatar, AvatarFallback } from 'components/ui/avatar';
import { ScrollArea } from 'components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from 'components/ui/dialog';
import { Label } from 'components/ui/label';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  Plus,
  Send,
  Clock,
  ArrowLeft,
  MessageCircle,
  Hash
} from 'lucide-react';

/* ─── GLOBAL STYLES (Cyberpunk Aesthetic) ────────────────────────────────── */
const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
  
  @keyframes scanLine { 0%{transform:translateY(-100%)} 100%{transform:translateY(100vh)} }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

  .dash-root { 
    font-family: 'DM Sans', sans-serif; 
    min-height: 100vh;
    background-color: #060810;
    background-image: url('/bg-dashboard.png'); /* Asegúrate de tener este fondo */
    background-size: cover;
    background-position: center top;
    background-attachment: fixed;
    position: relative;
    overflow-x: hidden;
    animation: fadeIn 0.4s ease;
  }

  .dash-root::before {
    content: ''; position: fixed; top: 0; left: 0; right: 0; height: 2px;
    background: linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.4), transparent);
    animation: scanLine 8s linear infinite; z-index: 1; pointer-events: none;
  }

  .main-content {
    position: relative; z-index: 2; padding: 32px 40px;
  }

  .glass-panel {
    background: rgba(10, 8, 30, 0.65);
    backdrop-filter: blur(24px) saturate(160%);
    -webkit-backdrop-filter: blur(24px) saturate(160%);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 20px;
    position: relative;
    transition: all 0.3s ease;
    box-shadow: 0 4px 32px rgba(0, 0, 0, 0.4), inset 0 0 0 0.5px rgba(255,255,255,0.05);
  }

  .title-neon {
    color: #FFF;
    text-shadow: 0 0 15px rgba(0, 255, 255, 0.4);
    letter-spacing: -0.02em;
  }

  /* ── GLOW EFFECTS ── */
  .glow-cyan-hover:hover { box-shadow: 0 0 30px rgba(0, 255, 255, 0.15); border-color: rgba(0, 255, 255, 0.4); }
  .glow-cyan { box-shadow: 0 0 20px rgba(0, 255, 255, 0.08); border-color: rgba(0, 255, 255, 0.2); }

  /* ── NEON BUTTONS ── */
  .btn-neon-cyan {
    background: #00FFFF; color: #000; font-weight: 800; border: none;
    box-shadow: 0 0 15px rgba(0, 255, 255, 0.4); transition: all 0.2s;
  }
  .btn-neon-cyan:hover { background: #66FFFF; box-shadow: 0 0 25px rgba(0, 255, 255, 0.6); transform: translateY(-1px); }
  
  .btn-neon-cyan:disabled {
    background: rgba(0, 255, 255, 0.2); color: rgba(255, 255, 255, 0.3); box-shadow: none; transform: none; cursor: not-allowed;
  }

  .cy-input {
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1);
    color: #fff; transition: all 0.3s;
  }
  .cy-input:focus {
    border-color: #00FFFF; box-shadow: 0 0 15px rgba(0,255,255,0.2); outline: none;
  }
`;

const Community = () => {
  const { user, api } = useAuth(); 
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  
  const [replyContent, setReplyContent] = useState('');
  const [replying, setReplying] = useState(false);

  const fetchThreads = useCallback(async () => {
    try {
      const response = await api.get('/community/threads');
      setThreads(response.data);
    } catch (error) {
      console.error('Error fetching threads:', error);
      toast.error('No se pudieron cargar las discusiones');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  const fetchThread = async (threadId) => {
    try {
      const response = await api.get(`/community/threads/${threadId}`);
      setSelectedThread(response.data);
    } catch (error) {
      console.error('Error fetching thread:', error);
      toast.error('Error al cargar la discusión');
    }
  };

  const createThread = async () => {
    if (!newTitle.trim() || !newContent.trim()) {
      toast.error('Por favor completa todos los campos');
      return;
    }
    
    try {
      await api.post('/community/threads', {
        title: newTitle,
        content: newContent
      });
      
      toast.success('Discusión creada');
      setCreateDialogOpen(false);
      setNewTitle('');
      setNewContent('');
      fetchThreads();
    } catch (error) {
      console.error('Error creating thread:', error);
      toast.error('Error al crear la discusión (403: No autorizado)');
    }
  };

  const sendReply = async () => {
    if (!replyContent.trim() || !selectedThread) return;
    
    setReplying(true);
    try {
      await api.post(`/community/threads/${selectedThread.id}/replies`, {
        content: replyContent
      });
      
      setReplyContent('');
      fetchThread(selectedThread.id);
      toast.success('Respuesta enviada');
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Error al enviar la respuesta');
    } finally {
      setReplying(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  };

  if (loading) {
    return (
      <div className="dash-root flex items-center justify-center text-white">
        <style>{GLOBAL_STYLES}</style>
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#00FFFF] border-t-transparent rounded-full animate-spin shadow-[0_0_15px_#00FFFF]" />
          <span className="text-sm font-bold tracking-widest text-[#00FFFF]">CARGANDO FORO...</span>
        </div>
      </div>
    );
  }

  if (selectedThread) {
    return (
      <div className="dash-root" data-testid="thread-detail">
        <style>{GLOBAL_STYLES}</style>
        <div className="main-content max-w-5xl mx-auto space-y-6">
          <Button 
            variant="ghost" 
            onClick={() => setSelectedThread(null)}
            className="text-[#00FFFF] hover:bg-[#00FFFF]/10 hover:text-[#00FFFF] font-bold tracking-wide transition-all -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a discusiones
          </Button>

          <Card className="glass-panel glow-cyan">
            <CardContent className="p-8">
              <div className="flex items-start gap-5">
                <Avatar className="w-12 h-12 border border-[#00FFFF]30 shadow-[0_0_15px_rgba(0,255,255,0.2)]">
                  <AvatarFallback className="bg-[#00FFFF]10 text-[#00FFFF] font-black text-lg">
                    {getInitials(selectedThread.user_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-black text-white text-base tracking-wide">{selectedThread.user_name}</span>
                    <span className="text-[10px] text-white/40 font-bold uppercase tracking-widest flex items-center gap-1.5">
                      <Clock className="w-3 h-3" />
                      {formatDate(selectedThread.created_at)}
                    </span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-black text-white mb-4 tracking-tight leading-tight">
                    {selectedThread.title}
                  </h2>
                  <p className="text-white/70 whitespace-pre-wrap leading-relaxed text-[15px]">
                    {selectedThread.content}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4">
            <h3 className="font-bold text-white flex items-center gap-2 uppercase text-[11px] tracking-widest pl-2">
              <MessageCircle className="w-4 h-4 text-[#FF00FF]" />
              {selectedThread.replies?.length || 0} Respuestas
            </h3>

            <ScrollArea className="h-[400px] pr-4 -mr-4">
              <div className="space-y-4">
                {selectedThread.replies?.map(reply => (
                  <Card key={reply.id} className="bg-black/40 border-white/10 rounded-2xl shadow-[inset_0_0_20px_rgba(255,255,255,0.02)]">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <Avatar className="w-10 h-10 border border-white/10">
                          <AvatarFallback className="bg-white/5 text-xs font-black text-white">
                            {getInitials(reply.user_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-bold text-[14px] text-white">{reply.user_name}</span>
                            <span className="text-[10px] text-white/30 font-bold uppercase tracking-widest">
                              {formatDate(reply.created_at)}
                            </span>
                          </div>
                          <p className="text-[14px] text-white/70 whitespace-pre-wrap leading-relaxed">
                            {reply.content}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>

            <Card className="glass-panel border-t-[#00FFFF]40 mt-4">
              <CardContent className="p-5">
                <div className="flex gap-4 items-start">
                  <Avatar className="w-10 h-10 border border-[#00FFFF]40 shadow-[0_0_10px_rgba(0,255,255,0.2)] hidden sm:flex">
                    <AvatarFallback className="bg-[#00FFFF]20 text-[#00FFFF] text-xs font-black">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 flex gap-3 flex-col sm:flex-row">
                    <Textarea
                      placeholder="Escribe tu respuesta..."
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      className="cy-input min-h-[60px] resize-none rounded-xl text-[14px] w-full"
                    />
                    <Button 
                      onClick={sendReply}
                      disabled={replying || !replyContent.trim()}
                      className="btn-neon-cyan h-auto py-3 px-6 rounded-xl flex items-center justify-center sm:self-stretch"
                    >
                      <Send className="w-5 h-5 sm:mr-0 mr-2" />
                      <span className="sm:hidden font-bold">Enviar Respuesta</span>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dash-root" data-testid="community-page">
      <style>{GLOBAL_STYLES}</style>
      <div className="main-content max-w-6xl mx-auto space-y-8">
        
        {/* ── HEADER ── */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h1 className="text-4xl font-black title-neon" style={{ fontFamily: 'Roberta, serif' }}>
              Comunidad <span className="text-[#00FFFF]">Makenai</span>
            </h1>
            <p className="text-white/40 text-[10px] uppercase tracking-[0.2em] font-bold mt-2">
              Conecta y comparte conocimiento con otros estudiantes
            </p>
          </div>
          
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-neon-cyan rounded-xl px-8 py-6 text-sm">
                <Plus className="w-5 h-5 mr-2" />
                Nueva Discusión
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0a0a0a]/95 backdrop-blur-xl border border-[#00FFFF]/30 shadow-[0_0_40px_rgba(0,255,255,0.15)] text-white sm:rounded-3xl">
              <DialogHeader>
                <DialogTitle className="text-2xl font-black text-white tracking-tight">Crear Nueva Discusión</DialogTitle>
                <DialogDescription className="text-white/50 text-xs font-bold uppercase tracking-widest mt-1">
                  Inicia un tema para debatir con la comunidad
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-5 pt-6">
                <div className="space-y-3">
                  <Label htmlFor="thread-title" className="text-[11px] font-black uppercase tracking-widest text-[#00FFFF]">Título</Label>
                  <Input
                    id="thread-title"
                    placeholder="¿Cuál es tu pregunta o tema?"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    className="cy-input rounded-xl h-12 text-[15px]"
                  />
                </div>
                <div className="space-y-3">
                  <Label htmlFor="thread-content" className="text-[11px] font-black uppercase tracking-widest text-[#00FFFF]">Contenido</Label>
                  <Textarea
                    id="thread-content"
                    placeholder="Describe tu pregunta o comparte tu conocimiento..."
                    value={newContent}
                    onChange={(e) => setNewContent(e.target.value)}
                    className="cy-input min-h-[140px] rounded-xl text-[15px] resize-none"
                  />
                </div>
                <Button 
                  onClick={createThread}
                  className="w-full btn-neon-cyan py-6 mt-4 rounded-xl text-[14px]"
                >
                  Publicar Ahora
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* ── THREADS LIST ── */}
        {threads.length > 0 ? (
          <div className="grid grid-cols-1 gap-5">
            {threads.map(thread => (
              <Card 
                key={thread.id}
                className="glass-panel glow-cyan-hover cursor-pointer group rounded-2xl overflow-hidden"
                onClick={() => fetchThread(thread.id)}
              >
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-start gap-5">
                    <Avatar className="w-14 h-14 border border-white/10 group-hover:border-[#00FFFF]40 transition-colors shadow-lg hidden sm:flex">
                      <AvatarFallback className="bg-white/5 group-hover:bg-[#00FFFF]10 group-hover:text-[#00FFFF] text-white font-black transition-colors text-lg">
                        {getInitials(thread.user_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2.5">
                        <span className="font-bold text-white text-[15px]">{thread.user_name}</span>
                        <div className="flex items-center gap-1.5 text-[10px] text-white/30 font-bold uppercase tracking-widest">
                          <Clock className="w-3 h-3" />
                          {formatDate(thread.created_at)}
                        </div>
                      </div>
                      <h3 className="text-xl md:text-2xl font-black text-white mb-3 group-hover:text-[#00FFFF] transition-colors tracking-tight line-clamp-1">
                        {thread.title}
                      </h3>
                      <p className="text-[14px] text-white/60 line-clamp-2 leading-relaxed">
                        {thread.content}
                      </p>
                      <div className="flex items-center gap-4 mt-5">
                        <Badge variant="secondary" className="bg-[#FF00FF]10 text-[#FF00FF] border border-[#FF00FF]30 px-3 py-1 text-[10px] font-black uppercase tracking-widest shadow-[0_0_10px_rgba(255,0,255,0.1)]">
                          <MessageSquare className="w-3.5 h-3.5 mr-2" />
                          {thread.replies_count} respuestas
                        </Badge>
                      </div>
                    </div>
                    <div className="pl-4 self-center">
                      <ArrowLeft className="w-6 h-6 text-white/10 group-hover:text-[#00FFFF] rotate-180 transition-all transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="glass-panel p-16 text-center rounded-3xl border border-dashed border-[#00FFFF]40">
            <CardContent className="flex flex-col items-center justify-center">
              <div className="w-24 h-24 bg-[#00FFFF]10 rounded-full flex items-center justify-center mb-6 border border-[#00FFFF]30 shadow-[0_0_30px_rgba(0,255,255,0.1)]">
                <Hash className="w-10 h-10 text-[#00FFFF]" />
              </div>
              <h3 className="text-3xl font-black text-white mb-3 tracking-tight">El foro está vacío</h3>
              <p className="text-white/50 max-w-md mx-auto mb-10 text-[15px] leading-relaxed">
                Sé la primera persona en iniciar un debate y comparte tus dudas con la comunidad.
              </p>
              <Button onClick={() => setCreateDialogOpen(true)} className="btn-neon-cyan px-10 py-6 rounded-xl text-[14px]">
                Crear la primera discusión
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Community;